import * as React from 'react';
import * as styles from './ImageCropper.css';
import Cropper from 'react-easy-crop';
import ImageService from '../../services/ImageService';
import { Environment } from '../../services/Environment';
import { IAButton } from '../button/Button';
import { DEFAULT_IMAGE_ASPECT_RATIO } from '../../global/Constants';
import { IASpinner } from '../spinner/Spinner';
import { IEnvironment } from '../../interfaces/IEnvironment';

export interface IAImageCropperProps {
  token?: string;
  tenant?: string;
  environment?: IEnvironment;
  height?: number;
  aspect?: number;
  showButtons?: boolean;
  enableZoom?: boolean;
  maxZoom?: number;
  image: string;
  initialCrop?: { x: number, y: number, width: number, height: number };
  onClick?: () => void;
  onUpdate: (croppedAreaPixels: { x: number, y: number, width: number, height: number }) => void;
  onCancel?: () => void;
  onSave?: (croppedAreaPixels: { x: number, y: number, width: number, height: number }) => void;
  onReset?: () => void;
  onUnsupportedFormat?: () => void;
  modified?: string;
  highlightColor?: string;
}

export interface IAImageCropperState {
  croppedAreaPixels?: { x: number, y: number, width: number, height: number },
  crop: { x: number, y: number },
  zoom: number,
  loading: boolean,
  checkingCrop: boolean,
  cropable?: boolean,
}

export class IAImageCropper extends React.Component<IAImageCropperProps, IAImageCropperState> {
  zoomSteps = 0.1;

  constructor(props: IAImageCropperProps) {
    super(props);
    this.state = {
      crop: { x: 0, y: 0 },
      zoom: 1,
      loading: true,
      checkingCrop: true,
    };

    this.getCropability();
  }

  componentDidMount() {
    let img: HTMLImageElement = document.getElementsByClassName("reactEasyCrop_Image")[0] as HTMLImageElement;
    img.onload = () => { this.setState({ loading: false }) }
  }

  async getCropability() {
    const cropable = await ImageService.getImageCropability(Environment.getEnvironmentForImageAPI(this.props.environment), this.props.tenant, this.props.token, this.props.image);

    this.setState({
      checkingCrop: false,
      cropable: cropable,
    });

    if (!cropable && this.props.onUnsupportedFormat) {
      this.props.onUnsupportedFormat();
    }
  }

  onZoomChange(zoom: number) {
    this.setState({ zoom: zoom })
  }

  zoomOut() {
    const zoom = this.state.zoom - this.zoomSteps;
    if (zoom <= 1) {
      this.setState({ zoom: 1 });
    } else {
      this.setState({ zoom: zoom });
    }
  }

  zoomIn() {
    const zoom = this.state.zoom + this.zoomSteps;
    let max = this.props.maxZoom ? this.props.maxZoom : 3;
    if (zoom >= max) {
      this.setState({ zoom: max });
    } else {
      this.setState({ zoom: zoom });
    }
  }

  render() {
    const loadingOrChecking = this.state.loading || this.state.checkingCrop;

    return (
      <div
        style={{ height: this.props.height ? this.props.height : 300 }}
        className={styles.IAImageCropper_Container}
        onClick={() => this.props.onClick()}
      >
        {this.props.showButtons && !loadingOrChecking &&
          <div
            className={styles.IAImageCropper_ButtonContainer}
          >
            {this.props.onSave &&
              <IAButton
                iconColor={this.props.highlightColor ? this.props.highlightColor : "#333333"}
                // mouseOverButtonColor="rgb(200 200 200 / 0.8)"
                icon="Checkmark"
                onClick={() => this.props.onSave(this.state.croppedAreaPixels)}
              />
            }
            {this.props.onCancel &&
              <IAButton
                iconColor={this.props.highlightColor ? this.props.highlightColor : "#333333"}
                // mouseOverButtonColor="rgb(200 200 200 / 0.8)"
                icon="Close"
                onClick={() => this.props.onCancel()}
              />
            }
            {this.props.onReset &&
              <IAButton
                iconColor={this.props.highlightColor ? this.props.highlightColor : "#333333"}
                // mouseOverButtonColor="rgb(200 200 200 / 0.8)"
                icon="Restart"
                onClick={() => this.props.onReset()}
              />
            }
          </div>
        }
        {this.props.enableZoom && !loadingOrChecking &&
          <div
            className={styles.IAImageCropper_ZoomContainer}
          >
            <IAButton
              iconColor={this.props.highlightColor ? this.props.highlightColor : "#333333"}
              // mouseOverButtonColor="rgb(200 200 200 / 0.8)"
              icon="ZoomOut"
              onClick={() => this.zoomOut()}
            />
            <IAButton
              iconColor={this.props.highlightColor ? this.props.highlightColor : "#333333"}
              // mouseOverButtonColor="rgb(200 200 200 / 0.8)"
              icon="ZoomIn"
              onClick={() => this.zoomIn()}
            />
          </div>
        }
        {(this.state.loading || this.state.checkingCrop) &&
          <div className={styles.IAImageCropper_SpinnerContainer}>
            <IASpinner color={this.props.highlightColor}/>
          </div>
        }
        <div style={{ opacity: loadingOrChecking ? 0 : 100 }}>
          <Cropper
            image={this.props.image}
            crop={this.state.crop}
            zoom={this.state.zoom}
            zoomSpeed={1}
            zoomWithScroll={!!this.props.enableZoom}
            aspect={this.props.aspect ?? DEFAULT_IMAGE_ASPECT_RATIO}
            // restrictPosition={false}
            onCropChange={(crop) => this.setState({ crop: crop })}
            onCropComplete={(_, croppedAreaPixels) => {
              this.setState({
                croppedAreaPixels: croppedAreaPixels
              });
              this.props.onUpdate(croppedAreaPixels);
            }}
            onZoomChange={(zoom) => this.onZoomChange(zoom)}
            initialCroppedAreaPixels={this.props.initialCrop}
          />
        </div>
      </div>
    );
  }
}
